import { useState } from 'react'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { formatDayLabel } from '@/lib/dates'
import { useAssignMeeting, useRemoveMeeting } from '@/queries/clientMeetings'
import type { CompanyRow, WaitingMeeting } from '@/types/clientMeetings'

export const selectClass =
  'h-[var(--etn-control-height-input)] w-full max-w-xs rounded-etn-pill border border-etn-border-input bg-etn-surface px-4 text-etn-control text-etn-text-cell'

function WaitingRow({ meeting, clients, allowDismiss }: { meeting: WaitingMeeting; clients: CompanyRow[]; allowDismiss: boolean }) {
  const [companyId, setCompanyId] = useState('')
  const [confirmingDismiss, setConfirmingDismiss] = useState(false)
  const assign = useAssignMeeting()
  const dismiss = useRemoveMeeting()

  return (
    <li className="space-y-2 border-b border-[var(--etn-gray-100)] py-3 last:border-b-0">
      <div>
        <div className="font-medium text-etn-text">{meeting.title}</div>
        <div className="text-[12.5px] text-etn-text-soft">{formatDayLabel(meeting.date)}</div>
        <div className="mt-1 break-words text-[12px] text-etn-text-label">{meeting.attendeeEmails.join(', ')}</div>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <select aria-label={`Client for ${meeting.title}`} className={selectClass} value={companyId} onChange={event => setCompanyId(event.target.value)}>
          <option value="">Pick a client</option>
          {clients.map(client => (
            <option key={client.companyId} value={client.companyId}>
              {client.name}
            </option>
          ))}
        </select>
        <Button size="sm" disabled={!companyId || assign.isPending} onClick={() => assign.mutate({ meetingId: meeting._id, companyId })}>
          Assign
        </Button>
        {allowDismiss && !confirmingDismiss && (
          <Button size="sm" variant="ghost" onClick={() => setConfirmingDismiss(true)}>
            Dismiss
          </Button>
        )}
      </div>
      {confirmingDismiss && (
        <div className="flex flex-wrap items-center gap-2 rounded-etn-card bg-etn-warning-subtle px-3 py-2 text-[12.5px] text-etn-warning-text">
          <span>Dismiss this call? It won't come back unless restored from Settings.</span>
          <Button size="xs" variant="destructive" disabled={dismiss.isPending} onClick={() => dismiss.mutate({ meetingId: meeting._id })}>
            Yes, dismiss it
          </Button>
          <Button size="xs" variant="ghost" onClick={() => setConfirmingDismiss(false)}>
            Cancel
          </Button>
        </div>
      )}
    </li>
  )
}

// Show waiting meetings by title, date and attendees only: their content stays hidden until a client is chosen.
export function AssignMeetingDialog({
  meetings,
  clients,
  allowDismiss,
  open,
  onOpenChange,
}: {
  meetings: WaitingMeeting[]
  clients?: CompanyRow[]
  allowDismiss: boolean
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Assign meetings</DialogTitle>
          <DialogDescription>
            {allowDismiss
              ? "These Fireflies calls include an Etainabl user but match no client. Pick the client, or dismiss a call that isn't a client call."
              : 'These Fireflies calls match more than one client. Pick the client each one belongs to.'}
          </DialogDescription>
        </DialogHeader>
        <ul>
          {meetings.map(meeting => (
            <WaitingRow key={meeting._id} meeting={meeting} clients={clients ?? meeting.candidates} allowDismiss={allowDismiss} />
          ))}
        </ul>
      </DialogContent>
    </Dialog>
  )
}

export const needsAssigningLabel = (count: number) => `${count} meeting${count === 1 ? ' needs' : 's need'} assigning`
